import React from "react"
import {
  ArrowForward
} from "@mui/icons-material"
import { TopBarRedesigned, AdditionalActionsIconType } from "components/TopBarRedesigned"
import DentistSideMenu from "components/DentistSideMenu"
import { useHistory } from 'react-router'
import { exitReact } from "utils"
import { openExternalUrl } from "utils/nativeApp"
import { Button, Box } from "@mui/material"
import MaterialInput from "components/Inputs/MaterialInput"
import {
  PageShell,
  MainContainer,
  ContentContainer,
  PageTitle,
  BluePaper,
  InfoPaper,
  SectionTitle,
  GuideLink,
  InfoTitle,
  InfoText,
} from './SecondStep.styles'

export interface SecondStepProps {
    titleValue?: string;
    titleValueError?: string;
    onTitleValueChange: (value: string) => void
    descriptionValue?: string;
    descriptionValueError?: string;
    onDescriptionValueChange: (value: string) => void
    practiceName?: string
    onBack: () => void
    onClose: () => void
    onSubmit: (event: React.FormEvent) => void
    descriptionEditor?: React.ReactNode
    showContinue?: boolean
    showStepProgress?: boolean
}

const JOB_POST_GUIDE_URL = "https://support.tempstars.com/hc/en-us/articles/31049914906899-How-to-Attract-Top-Talent-with-your-Job-Description-for-Dental-Offices";

const SecondStep: React.FC<SecondStepProps> = ({
    titleValue,
    titleValueError,
    onTitleValueChange,
    descriptionValue,
    descriptionValueError,
    onDescriptionValueChange,
    practiceName,
    onBack,
    onClose,
    onSubmit,
    descriptionEditor,
    showContinue = true,
    showStepProgress = false,
}) => {
  const history = useHistory();
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  // T2-6758: hamburger + side menu
  const [isSideMenuOpen, setIsSideMenuOpen] = React.useState(false);

  const openJobPostGuide = React.useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    openExternalUrl(JOB_POST_GUIDE_URL);
  }, []);

  const keepFocusedFieldVisible = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const target = event.target as HTMLElement;
    const field = target.closest('.MuiFormControl-root') as HTMLElement | null;

    const correctScroll = () => {
      // WKWebView may scroll the document itself when the keyboard opens.
      // Keep the header/safe-area fixed and only adjust this page's scroll body.
      window.scrollTo(0, 0);

      const scrollContainer = scrollContainerRef.current;
      if (!field || !scrollContainer) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const fieldRect = field.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const safeTop = containerRect.top + 16;
      const safeBottom = Math.min(containerRect.bottom, viewportHeight) - 20;

      if (fieldRect.top < safeTop) {
        scrollContainer.scrollBy({ top: fieldRect.top - safeTop, behavior: 'smooth' });
      } else if (fieldRect.bottom > safeBottom) {
        scrollContainer.scrollBy({ top: fieldRect.bottom - safeBottom, behavior: 'smooth' });
      }
    };

    requestAnimationFrame(correctScroll);
    window.setTimeout(correctScroll, 175);
    window.setTimeout(correctScroll, 375);
  }, []);
  
  return (
    <PageShell>
      <TopBarRedesigned
        onBack={() => {
          history.push({ pathname: 'dentistHome' })
        }}
        additionalActionsIconType={AdditionalActionsIconType.menu}
        onAdditionalActionsButtonClick={() => setIsSideMenuOpen(true)}
      />
      <DentistSideMenu isOpen={isSideMenuOpen} onClose={() => setIsSideMenuOpen(false)} />
      <MainContainer ref={scrollContainerRef}>
        <ContentContainer>
          <PageTitle variant="h5">
            Post permanent or contract positions!
          </PageTitle>

          {showStepProgress ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Box sx={{
                  width: 10,
                  height: 10,
                  bgcolor: "var(--success-green, #4CAF50)",
                  borderRadius: "50%",
                  display: "inline-block",
                }} />
                <InfoText variant="body2" sx={{ ml: 1, fontWeight: "medium" }}>
                  Step 2 of 2
                </InfoText>
              </Box>
              <Box sx={{
                width: "100%",
                height: "4px",
                bgcolor: "var(--neutral-200, #e0e0e0)",
                mb: 4,
                borderRadius: 2,
                position: "relative",
                overflow: "hidden",
              }}>
                <Box sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  bgcolor: "var(--primary-blue)",
                  borderRadius: 2,
                }} />
              </Box>
            </>
          ) : null}
          
          <BluePaper elevation={1}>
            <InfoTitle variant="h6" sx={{ mb: 2 }}>
              Ok, {practiceName ? practiceName : ""} - let's get a Title and Description written!
            </InfoTitle>
            <InfoText variant="body1" sx={{ mb: 2 }}>
              Write at least 15 words to describe the position. Include key details about the role, 
              highlight important qualifications, and explain why applicants should be interested in 
              applying to your practice.
            </InfoText>
            <GuideLink 
              href={JOB_POST_GUIDE_URL}
              onClick={openJobPostGuide}
              rel="noopener noreferrer"
            >
              Guide to Writing Great Job Posts
            </GuideLink>
          </BluePaper>
            
          <SectionTitle variant="subtitle1" gutterBottom>
            Job Title
          </SectionTitle>
          <Box sx={{ mb: 4 }}>
            <MaterialInput
              id="title"
              name="title"
              value={titleValue || ''}
              onChange={(e) => onTitleValueChange(e.target.value)}
              onFocus={keepFocusedFieldVisible}
              error={!!titleValueError}
              helperText={titleValueError}
              label="Enter job title"
            />
          </Box>
            
          <SectionTitle variant="subtitle1" gutterBottom>
            Job Description
          </SectionTitle>
          <Box sx={{ mb: 4 }}>
            {descriptionEditor || (
              <MaterialInput
                id="description"
                name="description"
                value={descriptionValue || ''}
                onChange={(e) => onDescriptionValueChange(e.target.value)}
                onFocus={keepFocusedFieldVisible}
                error={!!descriptionValueError}
                helperText={descriptionValueError}
                label="Enter job description"
                multiline={true}
                rows={7}
              />
            )}
          </Box>
          
          <InfoPaper elevation={1}>
            <InfoTitle variant="subtitle1" gutterBottom>
              Note:
            </InfoTitle>
            {descriptionEditor ? (
              <InfoText variant="body2">
                Writing Assist can tidy or enhance your description, and lets you compare
                each version before choosing what to save.
              </InfoText>
            ) : (
              <>
                <InfoText variant="body2">
                  After submission, we'll automatically check for spelling and grammar
                  issues, and suggest improvements to your job posting.
                </InfoText>
                <InfoText variant="body2">
                  We'll provide an enhanced version of your description that you can
                  choose to use if you prefer it. Both versions will be shown for you to compare.
                </InfoText>
              </>
            )}
            <InfoText variant="body2" sx={{ mb: 0 }}>
              Remember - It's free to post and you can contact candidates free of charge, there are no surprises or catches - and we don't charge anything for permanent hires - you're welcome!
            </InfoText>
          </InfoPaper>
            
          {showContinue ? <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={onSubmit}
            fullWidth
            sx={{
              bgcolor: "var(--primary-blue)",
              borderRadius: 10,
              py: 1.5,
              textTransform: "uppercase",
              fontWeight: "bold",
              boxShadow: 2,
              mt: 4,
              '&:hover': {
                bgcolor: "#002266",
              },
            }}
          >
            Continue
          </Button> : null}
        </ContentContainer>
      </MainContainer>
    </PageShell>
  )
}

export default SecondStep;
